import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'environments/environment';
import {
  MeteringPointGroup,
  MeteringPointReadingPoint,
  MeterReadingsExportParameters,
  MeteringPoint,
  ClimateCompensatedCO2Result,
  MeteringPointFunctionControlContract,
  TaxReturnAgreementParams
} from '@model';
import { SxErrorService } from 'app/services/sx-error.service';
import { MeteringPointFunctionControl } from 'app/models/metering-point-function-control';
import { MeteringPointDisruptions } from 'app/models/disruptionInfo.model';
import { AnalyzeMeterReadingType, AnalyzeUnitType, DatePeriodType, MeteringType } from '@enums';
import { MeteringPointIntelligyStatus } from 'app/models/meteringpoint-intelligy-status.model';
import { MeteringPointIntelligyOnboardStatus } from 'app/models/meteringpoint-intelligy-onboard-status.model';
import dayjs from 'dayjs';


@Injectable({
  providedIn: 'root'
})
export class MeteringPointService {
 
  constructor(private http: HttpClient, private sxErrorService: SxErrorService) {}

  getMeteringPointsOnUser(): Observable<MeteringPoint[]> {
    return this.http
      .get<MeteringPoint[]>(`${environment.apiEndpoint}/meteringpoints`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }
  
  getMeteringPointDisruptionsOnUser(fromDate:Date): Observable<MeteringPointDisruptions[]> {
    return this.http
      .get<MeteringPointDisruptions[]>(`${environment.apiEndpoint}/meteringpoints/Disruptions/${fromDate.toISOString()}`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMeteringPointReadingsOnMeteringPoint(
    masterMeteringPointId: number,
    meteringPointId: number,
    resolution: DatePeriodType
  ): Observable<MeteringPointReadingPoint[]> {
    if (masterMeteringPointId === meteringPointId) {
      return this.http
        .get<MeteringPointReadingPoint[]>(
          `${environment.apiEndpoint}/meteringpoints/${meteringPointId}/meterreadings/${resolution}`
        )
        .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
    }
    return this.http
      .get<MeteringPointReadingPoint[]>(
        `${environment.apiEndpoint}/meteringpoints/${masterMeteringPointId}/${meteringPointId}/meterreadings/${resolution}`
      )
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMeteringPointGroups(): Observable<MeteringPointGroup[]> {
    return this.http
      .get<MeteringPointGroup[]>(`${environment.apiEndpoint}/meteringpointgroups`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMeteringPointFunctionControls(
    masterMeteringPointId: number,
    meteringPointId: number
  ): Observable<MeteringPointFunctionControl[]> {
    if (masterMeteringPointId === meteringPointId) {
      return this.http
        .get<MeteringPointFunctionControl[]>(
          `${environment.apiEndpoint}/meteringpoints/${meteringPointId}/functioncontrols`
        )
        .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
    }
    return this.http
      .get<MeteringPointFunctionControl[]>(
        `${environment.apiEndpoint}/meteringpoints/${masterMeteringPointId}/${meteringPointId}/functioncontrols`
      )
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMeteringPointFunctionControlContract(
    masterMeteringPointId: number,
    meteringPointId: number
  ): Observable<MeteringPointFunctionControlContract> {
    if (masterMeteringPointId === meteringPointId) {
      return this.http
        .get<MeteringPointFunctionControlContract>(
          `${environment.apiEndpoint}/meteringpoints/${meteringPointId}/functioncontrolcontract`
        )
        .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
    }
    return this.http
    .get<MeteringPointFunctionControlContract>(
      `${environment.apiEndpoint}/meteringpoints/${masterMeteringPointId}/${meteringPointId}/functioncontrolcontract`
    )
    .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

 
  removeMeteringPointGroup(meteringPointGroup: MeteringPointGroup): Observable<MeteringPointGroup> {
    return this.http
      .delete<any>(`${environment.apiEndpoint}/meteringpointgroups/${meteringPointGroup.id}`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  addMeteringPointGroup(meteringPointGroup: MeteringPointGroup): Observable<MeteringPointGroup> {
    return this.http
      .post<MeteringPointGroup>(`${environment.apiEndpoint}/meteringpointgroups/`, meteringPointGroup)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  updateMeteringPointGroup(meteringPointGroup: MeteringPointGroup): Observable<MeteringPointGroup> {
    return this.http
      .post<MeteringPointGroup>(
        `${environment.apiEndpoint}/meteringpointgroups/${meteringPointGroup.id}`,
        meteringPointGroup
      )
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  bulkUpdateMeteringPointGroups(meteringPointGroups: MeteringPointGroup[]): Observable<MeteringPointGroup[]> {
    return this.http
      .post<MeteringPointGroup[]>(`${environment.apiEndpoint}/meteringpointgroups/bulkupdate`, meteringPointGroups)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  createReportKey(): Observable<string> {
    return this.http
      .get<string>(`${environment.apiEndpoint}/meteringpoints/key`)
      .pipe(catchError((error: any) => throwError(error.json())));
  }

  exportReadingsToExcel(key: string, exportSettings: MeterReadingsExportParameters): Observable<any> {
    if (!!exportSettings.masterMeteringPointId) {
      return this.http
        .get<any>(
          // tslint:disable-next-line:max-line-length
          `${environment.apiEndpoint}/reports/${key}/meteringpoints/${exportSettings.masterMeteringPointId}/${exportSettings.meteringPointId}/export/${exportSettings.periodType}`,
          {
            responseType: 'blob' as 'json'
          }
        )
        .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
    } else {
      return this.http
        .get<any>(
          `${environment.apiEndpoint}/reports/${key}/meteringpoints/${exportSettings.meteringPointId}/export/${exportSettings.periodType}`,
          {
            responseType: 'blob' as 'json'
          }
        )
        .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
    }
  }

  exportMeteringPointsToExcel(key: string, paneType: MeteringType, mpIds: number[]): Observable<any> {
    return this.http
      .post<any>(`${environment.apiEndpoint}/reports/${key}/meteringpoints/export/${paneType}`, mpIds, {
        responseType: 'blob' as 'json'
      })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getClimateCompensatedCO2(
    meteringPointId: number,
    customerId: string,
    from: Date,
    to: Date,
    resolution: string,
    type: string,
    unit: string,
    meteringType: MeteringType,
    compensationKeyFigure: number

  ): Observable<ClimateCompensatedCO2Result> {
    let fixedDateTo = dayjs(to)
      .add(1, 'd')
      .startOf('d')
      .toDate();

    return this.http
      .post<ClimateCompensatedCO2Result>(`${environment.apiEndpoint}/meteringpoints/${meteringPointId}/climatecompensatedco2`, {
        meteringPointId: meteringPointId,
        customerId: customerId,
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(fixedDateTo).format('YYYY-MM-DD'),
        resolution: resolution,
        type: type,
        unit: unit,
        meteringType: meteringType,
        compensationKeyFigure: compensationKeyFigure
      })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }
}
